import type { ClavePermiso } from '@plataforma/permisos';
import {
  BookOpen,
  Car,
  ClipboardList,
  Cog,
  CreditCard,
  FileSearch,
  Gauge,
  Gavel,
  Globe,
  Handshake,
  Inbox,
  type LucideIcon,
  Megaphone,
  MessageSquare,
  Package,
  Receipt,
  Scale,
  Scissors,
  TriangleAlert,
  Share2,
  ShieldCheck,
  ShoppingBag,
  Store,
  TrafficCone,
  TrendingUp,
  User,
  Users,
  Wallet,
  Wrench,
} from '@plataforma/ui/iconos';

/**
 * Menú por rol (arquitectura 2.15, sección 13): cada entrada pide el mismo permiso que su API,
 * así que nadie ve una pantalla que le respondería 403. Un mecánico ve Mis tareas y Vehículos; un
 * desarmador, Desarme y Piezas; un mostrador, Mostrador, Leads y Pedidos; el gerente casi todo.
 */
export interface Entrada {
  a: string;
  texto: string;
  permiso: ClavePermiso;
  /** Ícono del menú (arquitectura 2.16: navegación con íconos). */
  icono: LucideIcon;
}

/** La operación del negocio. */
export const MENU: Entrada[] = [
  { a: '/', texto: 'Tablero', permiso: 'vehiculos.ver', icono: Gauge },
  { a: '/subastas', texto: 'Subastas', permiso: 'montos.ver', icono: Gavel },
  { a: '/encargos', texto: 'Encargos', permiso: 'encargos.ver', icono: ShoppingBag },
  { a: '/vehiculos', texto: 'Vehículos', permiso: 'vehiculos.ver', icono: Car },
  {
    a: '/vehiculos?estado=transito_aduana',
    texto: 'Importación',
    permiso: 'importacion.ver',
    icono: TrafficCone,
  },
  {
    a: '/importacion/cotizaciones',
    texto: 'Valor en aduana',
    permiso: 'importacion.ver',
    icono: FileSearch,
  },
  {
    a: '/importacion/vigente',
    texto: 'Lo vigente',
    permiso: 'importacion.ver',
    icono: Scale,
  },
  { a: '/rentabilidad', texto: 'Rentabilidad', permiso: 'montos.ver', icono: TrendingUp },
  { a: '/taller', texto: 'Taller', permiso: 'ordenes.ver', icono: Wrench },
  {
    a: '/taller/mis-tareas',
    texto: 'Mis tareas',
    permiso: 'ordenes.trabajar',
    icono: ClipboardList,
  },
  { a: '/proveedores', texto: 'Proveedores', permiso: 'proveedores.ver', icono: Handshake },
  { a: '/desarme', texto: 'Desarme', permiso: 'piezas.administrar', icono: Scissors },
  { a: '/piezas', texto: 'Piezas', permiso: 'piezas.estado', icono: Cog },
  { a: '/mostrador', texto: 'Mostrador', permiso: 'mostrador.vender', icono: Receipt },
  {
    a: '/publicaciones',
    texto: 'Publicaciones',
    permiso: 'publicaciones.administrar',
    icono: Megaphone,
  },
  { a: '/canales', texto: 'Canales', permiso: 'publicaciones.administrar', icono: Share2 },
  { a: '/leads', texto: 'Leads', permiso: 'leads.atender', icono: MessageSquare },
  { a: '/pedidos', texto: 'Pedidos', permiso: 'pedidos.ver', icono: Package },
];

/** Lo del negocio mismo: usuarios para dueño y gerente; plan, sitio y dominio solo para el dueño. */
export const MENU_LOTERO: Entrada[] = [
  { a: '/usuarios', texto: 'Usuarios', permiso: 'usuarios.administrar', icono: Users },
  { a: '/negocio', texto: 'Mi negocio', permiso: 'lotero.administrar', icono: Store },
  { a: '/sitio', texto: 'Mi sitio', permiso: 'lotero.administrar', icono: Globe },
  { a: '/dominios', texto: 'Mi dominio', permiso: 'lotero.administrar', icono: Globe },
  { a: '/suscripcion', texto: 'Mi suscripción', permiso: 'lotero.administrar', icono: CreditCard },
];

/** Ayuda para toda persona del negocio (arquitectura 2.12). */
export const MENU_AYUDA: Entrada[] = [
  { a: '/ayuda', texto: 'Manual y preguntas', permiso: 'soporte.usar', icono: BookOpen },
  { a: '/ayuda/tickets', texto: 'Soporte', permiso: 'soporte.usar', icono: MessageSquare },
  { a: '/ayuda/privacidad', texto: 'Mi privacidad', permiso: 'soporte.usar', icono: ShieldCheck },
  // 2.27: solo dueño y gerente ven la bitácora de su negocio.
  { a: '/errores', texto: 'Errores', permiso: 'usuarios.administrar', icono: TriangleAlert },
];

/** Portal de Proveedores (arquitectura 2.8): el operador trabaja; el dueño además administra. */
export const MENU_PORTAL: Entrada[] = [
  { a: '/portal', texto: 'Inicio', permiso: 'portal.ver', icono: Gauge },
  { a: '/portal/convocatorias', texto: 'Convocatorias', permiso: 'portal.ver', icono: Inbox },
  { a: '/portal/trabajos', texto: 'Mis trabajos', permiso: 'portal.ver', icono: Wrench },
  { a: '/portal/cobros', texto: 'Cobros', permiso: 'portal.perfil', icono: Wallet },
  { a: '/portal/perfil', texto: 'Mi perfil', permiso: 'portal.perfil', icono: User },
  { a: '/portal/equipo', texto: 'Mi equipo', permiso: 'portal.perfil', icono: Users },
  { a: '/portal/errores', texto: 'Errores', permiso: 'portal.perfil', icono: TriangleAlert },
];

/** Entradas que el rol puede abrir. */
export const menuPara = (grupo: Entrada[], permitido: (clave: ClavePermiso) => boolean) =>
  grupo.filter((m) => permitido(m.permiso));
